import { useEffect, useMemo, useState } from "react"

import { Obj } from "scrivito"
import { loadScrivitoChildren } from "scrivito/utils/loadScrivitoChildren"
import { useForcedUpdate } from "shared/lib/useForcedUpdate"

interface UseScrivitoChildrenArgs<P extends Obj, T extends Obj> {
  page: P
  filterFn?: (child: T) => boolean
}

export function useScrivitoChildren<P extends Obj, T extends Obj>({
  page,
  filterFn
}: UseScrivitoChildrenArgs<P, T>) {
  const [value, forceUpdate] = useForcedUpdate()
  const [children, setChildren] = useState<T[]>(
    (page.orderedChildren() as T[]) || []
  )

  useEffect(() => {
    loadScrivitoChildren(page).then(result => {
      setChildren(result as T[])
      forceUpdate()
    })
  }, [page])

  const visibleChildren = useMemo(
    () => (filterFn ? children?.filter(child => filterFn(child)) : children),
    [page, children, value, filterFn]
  )

  return {
    children: children as T[],
    visibleChildren
  }
}
